import { useNavigate } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ProjectHeroThumb } from '@/components/projects/project-hero-thumb'
import { useMyFollowings } from '@/features/doer/use-doer'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { FollowingSummary } from '@/api/types'

const DoingProjectCard = ({ summary }: { summary: FollowingSummary }) => {
  const navigate = useNavigate()
  const outOfDate = summary.currentSourceVersion > summary.sourceVersion
  const startedAt = new Date(summary.startedAt).toLocaleDateString()
  // Source may have been deleted (sourceSlug null) — fall back to legacy id URL, the page will
  // surface the right "deleted source" state from there.
  const url = summary.sourceSlug
    ? canonicalProjectUrl(summary.sourceAuthor.handle, summary.sourceSlug)
    : `/projects/${summary.projectId}`
  return (
    <Card
      className="cursor-pointer transition-colors hover:bg-accent"
      onClick={() => navigate(url)}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(url)
        }
      }}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <ProjectHeroThumb
              thumbUrl={summary.heroThumbUrl ?? null}
              fallbackSeed={summary.projectId}
              alt={`${summary.sourceTitle} hero`}
            />
            <CardTitle className="truncate text-lg">{summary.sourceTitle}</CardTitle>
          </div>
          {outOfDate && (
            <Badge variant="outline" className="text-xs">
              Out of date
            </Badge>
          )}
        </div>
        <CardDescription>
          By @{summary.sourceAuthor.handle} · started {startedAt}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground">Progress</span>
          <span className="tabular-nums">
            {summary.completedCount}/{summary.totalActionCount} Actions
          </span>
        </div>
        {summary.nextUpStepTitle && (
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-muted-foreground">Next up</span>
            <span className="truncate">{summary.nextUpStepTitle}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const SkeletonGrid = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 3 }).map((_, i) => (
      <Skeleton key={i} className="h-44" />
    ))}
  </div>
)

const EmptyState = () => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <h2 className="text-lg font-semibold">Nothing in progress.</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        Browse public Projects and start one to begin tracking your progress here.
      </p>
    </CardContent>
  </Card>
)

export const DoingTabContent = () => {
  const { data, isLoading, isError, refetch, isFetching } = useMyFollowings()

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load your started projects</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>We couldn&apos;t reach the server. Try again?</span>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  if (isLoading) return <SkeletonGrid />

  const followings = data ?? []
  if (followings.length === 0) return <EmptyState />

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {followings.map((summary) => (
        <DoingProjectCard key={summary.projectId} summary={summary} />
      ))}
    </div>
  )
}
